<script setup lang="ts">
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import type { IChangeHistoryEntry } from '@modules/change-history/components/VChangeHistory/types';
import VCard from '@components/card/VCard/VCard.vue';
import VLoader from '@components/loader/VLoader.vue';
import { ADMIN_AFFILIATES_APPROVED_LIST_ROUTE_NAME } from '@modules/affiliates-approved/constants';
import VChangeHistory from '@modules/change-history/components/VChangeHistory/VChangeHistory.vue';
import { useModalPageWrapper } from '@modules/core/components/ModalPageWrapper/composables';
import ModalPageWrapper from '@modules/core/components/ModalPageWrapper/ModalPageWrapper.vue';
import { useFormatMonthDate } from '@modules/core/composables/datetime';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { isString } from '@utils/guards';

import type { INoneNegativeCarryoverFormComposableOptions } from '../../composables/useNoneNegativeCarryoverForm';
import type { INoneNegativeCarryoverForm } from '../../types';
import { useNoneNegativeCarryoverForm } from '../../composables';
import {
  ADMIN_AFFILIATE_NONE_NEGATIVE_CARRYOVER_ROUTE_NAME,
  AFFILIATES_NONE_NEGATIVE_CARRYOVER_PERMISSIONS,
  TRANSLATION_GROUP_AFFILIATES_NONE_NEGATIVE_CARRYOVER,
} from '../../constants';
import { useCreateNoneNegativeCarryoverMutation, useDeleteNoneNegativeCarryoverMutation } from '../../mutations';
import { useShowNoneNegativeCarryoverQuery } from '../../queries';
import { ADMIN_AFFILIATE_NONE_NEGATIVE_CARRYOVER_HISTORY_EDIT_ROUTE_NAME } from '../../submodules/history-edit/constants';
import AffiliateNoneNegativeCarryoverHistoryEditPage from '../../submodules/history-edit/pages/AffiliateNoneNegativeCarryoverHistoryEditPage/AffiliateNoneNegativeCarryoverHistoryEditPage.vue';

const { showPageDialog, closePageDialog } = useModalPageWrapper();
const router = useRouter();
const route = useRoute();
const { formatMonthDate } = useFormatMonthDate();
const currentDate = new Date();
const backToRoute = { name: ADMIN_AFFILIATES_APPROVED_LIST_ROUTE_NAME };

const affiliateId = computed(() => (isString(route.params.id) ? Number(route.params.id) : 0));
const itemId = computed(() => (isString(route.params.itemId) ? Number(route.params.itemId) : 0));

const { data: nncoData, isLoading: isNncoLoading, setData } = useShowNoneNegativeCarryoverQuery(affiliateId);

const {
  mutateAsync: createHistoryItem,
  data: createHistoryList,
  isLoading: isCreateLoading,
  errors,
  clearError,
} = useCreateNoneNegativeCarryoverMutation();
const { mutateAsync: deleteHistoryItem } = useDeleteNoneNegativeCarryoverMutation();

const historyOfNncoChanges = computed<IChangeHistoryEntry[] | undefined>(() => nncoData.value?.history_of_nncos_changes.map((item) => ({
  ...item,
  active: itemId.value === item.id,
})));

const formOptions = computed<INoneNegativeCarryoverFormComposableOptions>(() => ({
  enableNncoValue: nncoData.value?.enable_nnco,
  dateValue: currentDate,
  merchantNameValue: nncoData.value?.merchant_name,
}));

const { formSchema, formGroups } = useNoneNegativeCarryoverForm(formOptions);

function onHistoryEdit(entry: IChangeHistoryEntry) {
  void router.push({ name: ADMIN_AFFILIATE_NONE_NEGATIVE_CARRYOVER_HISTORY_EDIT_ROUTE_NAME, params: { itemId: entry.id } });
}

function updateHistoryOfNncoChanges(list?: IChangeHistoryEntry[]): void {
  setData((queryData) => {
    if (!queryData) {
      return queryData;
    }

    return {
      ...queryData,
      nnco: {
        ...queryData.nnco,
        history_of_nncos_changes: list ?? queryData.nnco?.history_of_nncos_changes,
      },
    };
  });
}

async function onHistoryDelete(entry: IChangeHistoryEntry): Promise<void> {
  try {
    await deleteHistoryItem(entry.id);

    updateHistoryOfNncoChanges(historyOfNncoChanges.value?.filter(({ id }) => id !== entry.id));

    if (itemId.value === entry.id) {
      await router.replace({ name: ADMIN_AFFILIATE_NONE_NEGATIVE_CARRYOVER_ROUTE_NAME });
    }
  } catch {}
}

async function onSubmit(values: INoneNegativeCarryoverForm) {
  try {
    await createHistoryItem({
      history_of_nncos_change: {
        ...values,
        date: formatMonthDate(values.date),
        affiliate_id: affiliateId.value,
      },
    });
    updateHistoryOfNncoChanges(createHistoryList.value?.history_of_nncos_changes);
  } catch {}
}
</script>

<template>
  <div v-data-qa-auto-id="'AffiliatesNoneNegativeCarryoverPage'">
    <ModalPageWrapper
      :show-dialog="showPageDialog"
      :back-to="backToRoute"
      @hide="closePageDialog"
    >
      <VCard
        type="form"
        class="rounded-b-none"
        :title="$getTranslation(TRANSLATION_GROUP_AFFILIATES_NONE_NEGATIVE_CARRYOVER, 'HEADER')"
      >
        <VForm
          :schema="formSchema"
          :groups="formGroups"
          :submit-btn-label="$getTranslation(TRANSLATION_GROUP_AFFILIATES_NONE_NEGATIVE_CARRYOVER, 'SUBMIT')"
          :custom-errors="errors"
          :is-loading="isCreateLoading"
          :readonly="!$hasPermission(AFFILIATES_NONE_NEGATIVE_CARRYOVER_PERMISSIONS.CREATE)"
          @submit="onSubmit"
          @interacted="clearError"
        />
      </VCard>

      <div class="rounded-b-xl border-t bg-white">
        <VLoader :loading="isNncoLoading">
          <VChangeHistory
            class="rounded-t-none"
            :list="historyOfNncoChanges"
            :edit-permission="AFFILIATES_NONE_NEGATIVE_CARRYOVER_PERMISSIONS.EDIT"
            :delete-permission="AFFILIATES_NONE_NEGATIVE_CARRYOVER_PERMISSIONS.DELETE"
            @edit="onHistoryEdit"
            @delete="onHistoryDelete"
          >
            <AffiliateNoneNegativeCarryoverHistoryEditPage :merchant-name="nncoData?.merchant_name" />
          </VChangeHistory>
        </VLoader>
      </div>
    </ModalPageWrapper>
  </div>
</template>
